import { useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import pageStyles from "~/features/ltl-shipment/index.module.css";
import { ShipmentTenderedRepModal } from "~/features/ltl-tender/components/shipment-tendered-rep-modal/shipment-tendered-rep-modal.tsx";
import { TenderShipmentDetails } from "~/features/ltl-tender/components/tender-shipment-details/tender-shipment-details.tsx";
import { useLtlStepLogic } from "~/features/ltl-tender/hooks/use-ltl-step-logic.ts";
import {
	useSeedActiveCustomer,
	useTenderModals,
} from "~/features/ltl-tender/hooks/use-tender-modals.ts";
import styles from "~/features/ltl-tender/index.module.css";
import { getLtlStepFromRoute } from "~/features/ltl-tender/utils/get-ltl-step-from-route.ts";
import { PrepayTenderModals } from "~/features/payment/components/prepay-tender-modals.tsx";
import { usePrepayTenderFlow } from "~/features/payment/hooks/use-prepay-tender-flow.ts";
import { BulkUploadButton } from "~/features/quote/components/bulk-upload-modal/bulk-upload-button.tsx";
import { ConfirmationModal } from "~/features/quote/components/shipment-details/components/confirmation-modal/confirmation-modal.tsx";
import { DetailsNeededBanner } from "~/features/quote/components/shipment-details/components/details-needed-banner/details-needed-banner.tsx";
import { TenderModal } from "~/features/quote/components/shipment-details/components/tender-modal/tender-modal.tsx";
import { getMissingDetailLabels } from "~/features/quote/components/shipment-details/utils/missing-detail-labels.ts";
import { buildTenderFailureMessage } from "~/features/quote/components/shipment-details/utils/tender-failure-message.ts";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { useSubmitTenderQuote } from "~/features/quote/hooks/use-shipment-summary-hooks.tsx";
import { useShipmentValidation } from "~/features/quote/hooks/use-shipment-validation.ts";
import { useQuoteBoardReturn } from "~/features/quote-board/hooks/use-quote-board-return.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { scrollMainContentToTop } from "~/shared/utils/scroll-to-top.ts";

import { CarrierRatesSummary } from "../quote/components/carrier-selection/components/carrier-rates-summary/carrier-rates-summary.tsx";
import { CarrierSelectionForm } from "../quote/components/carrier-selection/index.tsx";
import {
	CompletedHeader,
	CompletedHeaderType,
} from "../quote/components/completed-header/completed-header.tsx";
import { HeaderField } from "../quote/components/completed-header/components/header-field.tsx";
import { QuoteDetailsSummary } from "../quote/components/quote-details/components/quote-details-summary/quote-details-summary.tsx";
import type { Step } from "../quote/components/quoting-accordion/quoting-accordion.tsx";
import { QuotingAccordion } from "../quote/components/quoting-accordion/quoting-accordion.tsx";
import {
	QuoteDetailsContext,
	QuoteDetailsProvider,
} from "../quote/context/quote-details-context.tsx";

const LtlShipmentContent = () => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { validationErrors, validate } = useShipmentValidation();
	const navigate = useNavigate();
	const { quoteId } = useParams<{ quoteId: string }>();
	const location = useLocation();
	const currentStep = getLtlStepFromRoute(location.pathname);
	const { returnToQuoteBoard } = useQuoteBoardReturn();
	const { isInternal } = useDeploymentType();
	const { canRevisitCarrierSelection, disableTenderButton, selectedCarrier } =
		useLtlStepLogic(quoteState, currentStep);

	const {
		showModal,
		errorMessage,
		formResetKey,
		handleShowConfirmationModal,
		handleShowErrorModal,
		handleShowTenderModal,
		handleShowAccountRepModal,
	} = useTenderModals(routes.ltlShipment);
	const handleSaveSuccess = useCallback(() => {
		if (returnToQuoteBoard()) return;
		handleShowConfirmationModal(true);
	}, [handleShowConfirmationModal, returnToQuoteBoard]);
	const navigationState = location.state as {
		detailsNeededRepair?: boolean;
	} | null;
	const isDetailsNeededRepair =
		navigationState?.detailsNeededRepair === true && currentStep === 1;
	const allMissingDetails = useMemo(
		() =>
			getMissingDetailLabels(
				validationErrors,
				quoteState.items.map((item) => item.id),
			),
		[quoteState.items, validationErrors],
	);
	const missingDetails = isDetailsNeededRepair ? allMissingDetails : [];
	const canReviseCarrierSelection =
		!isDetailsNeededRepair || selectedCarrier != null;
	const hasUnresolvedDetails = missingDetails.length > 0;
	const tenderFailure = useMemo(
		() => buildTenderFailureMessage(errorMessage, allMissingDetails),
		[allMissingDetails, errorMessage],
	);
	const handleTenderError = useCallback(
		(message: string) => {
			validate(quoteState, selectedAddressBookItems);
			handleShowErrorModal(true, message);
		},
		[handleShowErrorModal, quoteState, selectedAddressBookItems, validate],
	);

	useEffect(() => {
		if (!isDetailsNeededRepair || quoteState.id <= 0) return;
		validate(quoteState, selectedAddressBookItems);
	}, [isDetailsNeededRepair, quoteState, selectedAddressBookItems, validate]);

	useSeedActiveCustomer();

	// Pre-paid customers must pay (ACH/credit card) before the carrier-selection
	// "Tender" (book + tender) finishes. When that combined call returns
	// PaymentRequired, hand off to the prepay flow; once payment succeeds the
	// flow runs the final tender call and we show the existing shipping-docs
	// modal. This mirrors the normal /shipment-details tender flow.
	const { submitTenderQuote } = useSubmitTenderQuote();
	const prepayFlow = usePrepayTenderFlow({
		submitTenderQuote,
		onPaymentError: (message) => handleShowErrorModal(true, message),
		onTenderError: handleTenderError,
		onTenderSuccess: () => handleShowTenderModal(true),
	});
	useAutoSpinner(prepayFlow.isProcessing);

	// Reset the scroll position whenever the user moves between the
	// shipment-details and carrier-selection steps (e.g. clicking Tender
	// navigates to /ltl-shipment/carrier-selection/{id}). Without this the
	// accordion keeps the previous step's scroll offset, so the carrier-selection
	// page opens partway down instead of at the top.
	useEffect(() => {
		scrollMainContentToTop();
	}, [currentStep]);

	const steps: Step[] = [
		{
			step: 1,
			id: "shipment-details",
			title: "Shipment Details",
			active: currentStep === 1,
			completed: currentStep > 1,
			header: (
				<div className={"completed-header"}>
					{quoteState.id ? (
						<HeaderField isLoading={false} title={"Quote ID:"}>
							{quoteState.id}
						</HeaderField>
					) : null}
					<BulkUploadButton />
				</div>
			),
			completedHeader: (
				<CompletedHeader
					onClick={() => {
						const routeQuoteId = quoteState.id
							? quoteState.id.toString()
							: quoteId;

						if (routeQuoteId) {
							navigate(`${routes.ltlShipment}/${routeQuoteId}`);
						} else {
							navigate(routes.ltlShipment);
						}
					}}
					quoteState={quoteState}
					type={CompletedHeaderType.QuoteDetails}
				/>
			),
			completedContent: <QuoteDetailsSummary />,
			content: (
				<>
					<DetailsNeededBanner missingDetails={missingDetails} />
					<TenderShipmentDetails
						key={formResetKey}
						disableSubmitButton={disableTenderButton}
						isRepairMode={isDetailsNeededRepair}
						onPaymentRequired={(quote) => prepayFlow.startPrepayFlow(quote)}
						onSaveError={(message) => handleShowErrorModal(true, message)}
						onSaveSuccess={handleSaveSuccess}
						onTenderError={handleTenderError}
						onTenderedWithDocs={() => handleShowTenderModal(true)}
						onVendorTenderedToRep={() => handleShowAccountRepModal(true)}
					/>
				</>
			),
		},
		{
			step: 2,
			id: "carrier-selection",
			title: "Carrier Selection",
			active: currentStep === 2,
			completed: currentStep === 1 && canRevisitCarrierSelection,
			completedHeader: (
				<CompletedHeader
					canRevise={canReviseCarrierSelection}
					disableRevise={hasUnresolvedDetails}
					onClick={() => {
						const routeQuoteId = quoteState.id
							? quoteState.id.toString()
							: quoteId;

						if (routeQuoteId) {
							navigate(
								routes.ltlShipmentCarrierSelection.replace(
									":quoteId",
									routeQuoteId,
								),
							);
						}
					}}
					reviseDisabledTitle={
						"Resolve the details needed on this shipment before revising the carrier selection."
					}
					selectedCarrier={selectedCarrier}
					type={CompletedHeaderType.CarrierRates}
				/>
			),
			completedContent: <CarrierRatesSummary />,
			content: (
				<CarrierSelectionForm
					enableShipmentValueModal={true}
					enableTenderOnBook={true}
					onPaymentRequired={(quote) => prepayFlow.startPrepayFlow(quote)}
					onTenderError={handleTenderError}
					onTenderSuccess={() => handleShowTenderModal(true)}
				/>
			),
		},
	];

	return (
		<>
			{showModal.confirmation ? (
				<ConfirmationModal
					onToggleModal={() => handleShowConfirmationModal(false)}
					showModal={showModal.confirmation}
				/>
			) : null}
			<PrepayTenderModals flow={prepayFlow} />
			{showModal.tender ? (
				<TenderModal
					onToggleModal={() => handleShowTenderModal(false)}
					showModal={showModal.tender}
				/>
			) : null}
			{showModal.accountRep ? (
				<ShipmentTenderedRepModal
					onClose={() => handleShowAccountRepModal(false)}
				/>
			) : null}
			{showModal.error ? (
				<TraxModal
					onClose={() => handleShowErrorModal(false)}
					title={"Tender Failed"}
					type={"error"}
					width={500}
				>
					<p>{tenderFailure.message}</p>
					{tenderFailure.missingDetails.length > 0 ? (
						<ul
							className={pageStyles.tenderErrorDetails}
							data-testid={"tender-error-missing-details"}
						>
							{tenderFailure.missingDetails.map((detail) => (
								<li key={detail}>{detail}</li>
							))}
						</ul>
					) : null}
				</TraxModal>
			) : null}
			<TraxPageHeader
				representativeType={"ltl"}
				showSupportMessage={!isInternal}
				title={"Tender LTL Shipment"}
			/>
			<QuotingAccordion steps={steps} />
		</>
	);
};

export const LtlShipment = () => {
	return (
		<div
			className={`${styles.ltlShipmentWrapper} ${pageStyles.mobileLayoutWrapper}`}
		>
			<QuoteDetailsProvider>
				<LtlShipmentContent />
			</QuoteDetailsProvider>
		</div>
	);
};
